import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  email: "",
  phone: "",
  destination: "",
  travel_month: "",
  travellers: "",
  travel_style: "",
  budget: "",
  message: "",
};

const inputClass = "input-flush";
const labelClass = "label text-steel";

function Field({ label, id, children }) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export default function EnquiryForm() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so we can respond.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — your enquiry is on its way, and a confirmation is headed to your inbox.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Reveal>
      <form data-testid="enquiry-form" onSubmit={submit} className="space-y-10">
        <div className="grid gap-10 md:grid-cols-2">
          <Field label="Full Name *" id="full_name">
            <input id="full_name" data-testid="enquiry-name" className={inputClass} value={form.full_name} onChange={set("full_name")} placeholder="Your name" />
          </Field>
          <Field label="Email *" id="email">
            <input id="email" type="email" data-testid="enquiry-email" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@example.com" />
          </Field>
          <Field label="Phone" id="phone">
            <input id="phone" data-testid="enquiry-phone" className={inputClass} value={form.phone} onChange={set("phone")} placeholder="+91" />
          </Field>
          <Field label="Destination" id="destination">
            <select id="destination" data-testid="enquiry-destination" className={inputClass} value={form.destination} onChange={set("destination")}>
              <option value="">Select a destination</option>
              <option>Sikkim</option>
              <option>Arunachal Pradesh</option>
              <option>Bali</option>
              <option>Dubai</option>
              <option>Somewhere else</option>
            </select>
          </Field>
          <Field label="Preferred Travel Month" id="travel_month">
            <input id="travel_month" data-testid="enquiry-month" className={inputClass} value={form.travel_month} onChange={set("travel_month")} placeholder="e.g. October 2026" />
          </Field>
          <Field label="Number of Travellers" id="travellers">
            <input id="travellers" data-testid="enquiry-travellers" className={inputClass} value={form.travellers} onChange={set("travellers")} placeholder="e.g. 2 adults" />
          </Field>
          <Field label="Travel Style" id="travel_style">
            <select id="travel_style" data-testid="enquiry-style" className={inputClass} value={form.travel_style} onChange={set("travel_style")}>
              <option value="">Select a style</option>
              <option>Mountain Escapes</option>
              <option>Cultural Journeys</option>
              <option>Luxury Escapes</option>
              <option>Leisure &amp; Discovery</option>
              <option>Not sure yet</option>
            </select>
          </Field>
          <Field label="Approximate Budget" id="budget">
            <select id="budget" data-testid="enquiry-budget" className={inputClass} value={form.budget} onChange={set("budget")}>
              <option value="">Select a range</option>
              <option>Under ₹50,000</option>
              <option>₹50,000 – ₹1,00,000</option>
              <option>₹1,00,000 – ₹2,50,000</option>
              <option>Above ₹2,50,000</option>
              <option>Prefer to discuss</option>
            </select>
          </Field>
        </div>
        <Field label="Tell us about your journey" id="message">
          <textarea
            id="message"
            data-testid="enquiry-message"
            rows={4}
            className={`${inputClass} resize-none`}
            value={form.message}
            onChange={set("message")}
            placeholder="Where would you like to go, and what would you love to experience?"
          />
        </Field>
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="submit"
            data-testid="enquiry-submit"
            disabled={sending}
            className="btn-primary disabled:opacity-60"
          >
            {sending ? "Sending…" : "Start Planning"}
          </button>
          <p className="text-xs tracking-wide text-steel">We read every enquiry personally.</p>
        </div>
      </form>
    </Reveal>
  );
}
